import type { SortingKey } from '../types/SortingKey.types';

// `undefined`/`null` are part of the contract, not an oversight: callers pass optional filters
// straight through and isQueryableValue() drops them before they reach the query string. The named
// keys have to spell `| null` out themselves — a declared property overrides the index signature,
// so without it `{ sort: null }` is rejected while `{ anythingElse: null }` is accepted.
export type UrlQueryParameter = {
    page?: number | null;
    perPage?: number | null;
    per_page?: number | null; // TODO: deprecated. Can be removed, since BE already supports 'perPage'
    sort?: SortingKey | null;
    order?: 'asc' | 'desc' | null;
    query?: string | null;
} & Record<string, string | number | boolean | string[] | number[] | undefined | null>;

export function buildUrlQuery(params?: UrlQueryParameter): string {
    return params
        ? Object.keys(params)
              .filter((key) => isQueryableValue(params[key]))
              .reduce<string>((prev, curr) => {
                  const param = params[curr];
                  /* TODO: The following lines will check if the param looks like this 'paramKey: "[firstValue, secondValue, ...]"'
                   * The paramKey value is a string which looks like an array. If this is the case, then we want to convert it to
                   * a real array.
                   * This needs to be refactored and is necessary because of some filtering. (E.g. filtering event-requests for
                   * multiple states at the same time ('?state[]=state1&state[]=state2&...')
                   * Maybe this logic can sit in the FilterPopup or Table component!
                   */
                  if (param instanceof Array) {
                      const paramArray = param
                          .filter(isQueryableValue)
                          .map((paramArrayValue) => `${curr}[]=${encodeQueryValue(paramArrayValue)}`)
                          .join('&');
                      return `${prev}&${paramArray}`;
                  }
                  // if (param.startsWith('[') && param.endsWith(']')) {
                  //
                  // }
                  return `${prev}&${curr}=${encodeQueryValue(params[curr])}`;
              }, '')
              .replace(/&/, '?')
        : '';
}

/**
 * CIN-14426: `NaN` and `±Infinity` stringify to 'NaN'/'Infinity'/'-Infinity', so a value computed
 * from a not-yet-loaded id used to be forwarded verbatim and the API answered 400 — e.g.
 * `/cinema-rooms?cinemaId=NaN`. Non-finite numbers are therefore omitted like `undefined`, `null`
 * and '' are. Arrays are kept as long as at least one of their entries survives.
 */
function isQueryableValue(value: unknown): boolean {
    if (value === undefined || value === null) {
        return false;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (value instanceof Array) {
        return value.some(isQueryableValue);
    }
    return String(value).length > 0;
}

function encodeQueryValue(value: string | number | boolean | string[] | number[] | undefined | null) {
    if (typeof value === 'string') {
        return encodeURIComponent(value);
    }
    return value;
}
